import { useState } from "react";
import { Col, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import {
  communicationStyles,
  CompanyLanguages,
  CompanyType,
  companyStatuses,
  getFallbackLanguage,
  hasLanguageStatus,
  languages,
  LanguageCode,
  LanguageStatus,
  setDefaultLanguage,
  setLanguageStatus,
} from "../../models/company";

function CopyableValue({ label, value }: { label: string; value?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <span
      className="d-inline-flex align-items-center gap-1 text-nowrap border rounded-pill bg-body-tertiary px-2"
      style={{ fontSize: "0.75rem", lineHeight: 1.6 }}
      role={value ? "button" : undefined}
      onClick={handleCopy}
      title={value ? (copied ? "Copied!" : "Click to copy") : undefined}
    >
      <span className="text-muted">{label}</span>
      <span className="font-monospace">{value || "-"}</span>
      {value && <i className={`bi ${copied ? "bi-check2 text-success" : "bi-copy text-muted"}`} />}
    </span>
  );
}

interface CompanyInformationProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  isNew: boolean;
  setCompany: (company: CompanyType) => void;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

function CompanyInformation({
  company,
  originalCompany,
  isNew,
  setCompany,
  hasFieldChanged,
  ChangedIndicator,
  readOnly = false,
}: CompanyInformationProps) {
  const updateLanguages = (nextLanguages: CompanyLanguages) => {
    setCompany({ ...company, info: { ...company.info!, languages: nextLanguages } });
  };

  const handleDefaultChange = (langId: LanguageCode) => {
    updateLanguages(setDefaultLanguage(company.info?.languages, langId));
  };

  const handleStatusToggle = (langId: LanguageCode, status: LanguageStatus | null) => {
    updateLanguages(setLanguageStatus(company.info?.languages, langId, status));
  };

  return (
    <Form>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Technical Name
              {!isNew && hasFieldChanged(company.name, originalCompany?.name) && <ChangedIndicator />}
            </Form.Label>
            <Form.Control
              type="text"
              value={company.name}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
              disabled={!isNew}
            />
            {isNew && <Form.Text className="text-muted">Unique identifier for the company</Form.Text>}
            {!isNew && (
              <div className="mt-1 d-flex justify-content-between gap-2 overflow-hidden">
                <CopyableValue label="Legacy ID" value={company.legacy_data?.id} />
                <CopyableValue label="UID" value={company.id} />
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Status
              {hasFieldChanged(company.status, originalCompany?.status) && <ChangedIndicator />}
            </Form.Label>
            <Form.Select
              value={company.status}
              onChange={(e) => setCompany({ ...company, status: e.target.value as any })}
              disabled={readOnly}
            >
              {companyStatuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Display Name
              {hasFieldChanged(company.info?.name, originalCompany?.info?.name) && <ChangedIndicator />}
            </Form.Label>
            <Form.Control
              type="text"
              value={company.info?.name || ""}
              onChange={(e) =>
                setCompany({
                  ...company,
                  info: { ...company.info!, name: e.target.value },
                })
              }
              readOnly={readOnly}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Email
              {hasFieldChanged(company.info?.email, originalCompany?.info?.email) && <ChangedIndicator />}
            </Form.Label>
            <Form.Control
              type="email"
              value={company.info?.email || ""}
              onChange={(e) =>
                setCompany({
                  ...company,
                  info: { ...company.info!, email: e.target.value },
                })
              }
              readOnly={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Label>
            Languages{" "}
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip>
                  Translatable: can be translated in Watchtower and the feedback tool, but not sent out yet. Live:
                  translatable and actually used in production. Default: the live language used as fallback (exactly
                  one language is Default). A language with nothing ticked is unavailable everywhere.
                </Tooltip>
              }
            >
              <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
            </OverlayTrigger>
            {hasFieldChanged(company.info?.languages || {}, originalCompany?.info?.languages || {}) && (
              <ChangedIndicator />
            )}
          </Form.Label>
          <div className="border rounded overflow-hidden">
            <Table striped size="sm" className="mb-0 align-middle">
              <thead>
                <tr className="table-light">
                  <th></th>
                  <th className="fw-normal small text-muted text-center">Default</th>
                  <th className="fw-normal small text-muted text-center">Live</th>
                  <th className="fw-normal small text-muted text-center">Translatable</th>
                </tr>
              </thead>
              <tbody>
                {languages.map((lang) => {
                  const langId = lang.id as LanguageCode;
                  const status = company.info?.languages?.[langId] ?? null;
                  const isDefault = status === "default";
                  const isLive = hasLanguageStatus(status, "live");

                  return (
                    <tr key={langId} className="border-top">
                      <td>{lang.name}</td>
                      <td className="text-center">
                        <Form.Check
                          type="radio"
                          name="company-default-language"
                          className="d-inline-block"
                          checked={isDefault}
                          disabled={readOnly}
                          onChange={() => handleDefaultChange(langId)}
                          aria-label={`${lang.name} is the default language`}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          className="d-inline-block"
                          checked={isLive}
                          disabled={readOnly || isDefault}
                          title={isDefault ? "Implied by Default" : undefined}
                          onChange={(e) => handleStatusToggle(langId, e.target.checked ? "live" : "translatable")}
                          aria-label={`${lang.name} is live`}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          className="d-inline-block"
                          checked={hasLanguageStatus(status, "translatable")}
                          disabled={readOnly || isLive}
                          title={isLive ? "Implied by Live" : undefined}
                          onChange={(e) => handleStatusToggle(langId, e.target.checked ? "translatable" : null)}
                          aria-label={`${lang.name} is translatable`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {!getFallbackLanguage(company.info?.languages) && (
            <div className="text-muted small mt-2">No default language set — pick the fallback language.</div>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Communication Style
              {hasFieldChanged(
                company.config_design_temp?.communication_style,
                originalCompany?.config_design_temp?.communication_style,
              ) && <ChangedIndicator />}
            </Form.Label>
            <Form.Select
              value={company.config_design_temp?.communication_style || ""}
              onChange={(e) =>
                setCompany({
                  ...company,
                  config_design_temp: {
                    ...company.config_design_temp,
                    communication_style: e.target.value as "formal" | "informal",
                  },
                })
              }
              disabled={readOnly}
            >
              <option value="">-- Select --</option>
              {communicationStyles.map((style) => (
                <option key={style.id} value={style.id}>
                  {style.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Website URL{" "}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    This field will be decommissioned in favour of the Links &gt; Website URL field. Please maintain it on both locations for now.
                  </Tooltip>
                }
              >
                <i className="bi bi-info-circle" style={{ cursor: "pointer", color: "#6c757d" }}></i>
              </OverlayTrigger>
              {hasFieldChanged(company.info?.website_url, originalCompany?.info?.website_url) && <ChangedIndicator />}
            </Form.Label>
            <Form.Control
              type="url"
              value={company.info?.website_url || ""}
              onChange={(e) =>
                setCompany({
                  ...company,
                  info: { ...company.info!, website_url: e.target.value },
                })
              }
              readOnly={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Group>
            <Form.Label>
              Comments
              {hasFieldChanged(company.info?.comments, originalCompany?.info?.comments) && <ChangedIndicator />}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={company.info?.comments || ""}
              onChange={(e) =>
                setCompany({
                  ...company,
                  info: { ...company.info!, comments: e.target.value },
                })
              }
              readOnly={readOnly}
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
}

export default CompanyInformation;
